// mindmetal-app/web/src/pages/employee/mindmetal/RenderingTab.tsx
import { useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Eye, Wand2, Copy, Check, FileText, Fingerprint } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, Select, Badge, api } from '@personic/shared'

const PRODUCT_CONTEXTS = [
  { value: 'engage', label: 'Engage' },
  { value: 'legal', label: 'Legal' },
  { value: 'clinical', label: 'Clinical' },
  { value: 'education', label: 'Education' },
  { value: 'finance', label: 'Finance' },
]

const SENSITIVITY_MODES = [
  { value: '', label: 'Default' },
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
  { value: 'minimal', label: 'Minimal' },
]

interface RenderedPersona {
  canonical_id: string
  coverage_level: string
  product_context: string
  document: string
  persona_document?: string
}

export function RenderingTab() {
  const [profileId, setProfileId] = useState('')
  const [productContext, setProductContext] = useState('engage')
  const [sensitivityMode, setSensitivityMode] = useState('')
  const [copied, setCopied] = useState(false)

  // Fetch test users for the profile selector
  const { data: testUsersData } = useQuery({
    queryKey: ['mindmetal-test-users-rendering'],
    queryFn: async () => {
      const { data } = await api.get('/api/mindmetal/v3/admin/test-users?page=1&limit=50')
      return data as {
        test_users: Array<{ canonical_id: string; display_name: string }>
      }
    },
  })

  const profileOptions = [
    { value: '', label: 'Select a profile...' },
    ...(testUsersData?.test_users.map((u) => ({
      value: u.canonical_id,
      label: `${u.display_name} (${u.canonical_id})`,
    })) || []),
  ]

  const productContextOptions = [
    { value: '', label: 'Select context...' },
    ...PRODUCT_CONTEXTS,
  ]

  // Render persona mutation
  const renderMutation = useMutation({
    mutationFn: async ({
      profile_id,
      product_context,
      sensitivity_mode,
    }: {
      profile_id: string
      product_context: string
      sensitivity_mode?: string
    }) => {
      const { data } = await api.post<RenderedPersona>('/admin/mindmetal/render-persona', {
        profile_id,
        product_context,
        sensitivity_mode: sensitivity_mode || undefined,
      })
      return data
    },
  })

  const handleRender = () => {
    if (!profileId || !productContext) return
    renderMutation.mutate({ profile_id: profileId, product_context: productContext, sensitivity_mode: sensitivityMode })
  }

  const handleCopy = async () => {
    if (!renderMutation.data?.document) return
    await navigator.clipboard.writeText(renderMutation.data.document)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const persona = renderMutation.data

  return (
    <div className="space-y-6">
      {/* Input Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-copper-600" />
            Persona Rendering Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-warm-500 mb-6">
            Select a profile and product context to render a persona document preview.
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            <Select
              label="Profile"
              options={profileOptions}
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
            />
            <Select
              label="Product Context"
              options={productContextOptions}
              value={productContext}
              onChange={(e) => setProductContext(e.target.value)}
            />
            <Select
              label="Sensitivity Mode"
              options={SENSITIVITY_MODES}
              value={sensitivityMode}
              onChange={(e) => setSensitivityMode(e.target.value)}
            />
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              onClick={handleRender}
              isLoading={renderMutation.isPending}
              disabled={!profileId || !productContext}
              leftIcon={<Wand2 className="w-4 h-4" />}
            >
              Render Persona
            </Button>
            {persona && (
              <Button variant="ghost" onClick={handleCopy}>
                {copied ? (
                  <Check className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {renderMutation.isError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {(renderMutation.error as any)?.response?.data?.detail ||
            (renderMutation.error as Error).message ||
            'Failed to render persona'}
        </div>
      )}

      {/* Rendered Result */}
      {persona && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-copper-600" />
                Rendered Persona
              </CardTitle>
              <div className="flex items-center gap-3">
                <Badge variant="copper" size="sm">
                  {persona.product_context}
                </Badge>
                <Badge
                  variant={
                    persona.coverage_level === 'mature'
                      ? 'sage'
                      : persona.coverage_level === 'established'
                        ? 'success'
                        : persona.coverage_level === 'emerging'
                          ? 'warning'
                          : 'default'
                  }
                  size="sm"
                >
                  {persona.coverage_level}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* Profile Info */}
              <div className="flex items-center gap-3 mb-4 p-3 bg-warm-50 rounded-xl">
                <Fingerprint className="w-5 h-5 text-copper-500" />
                <div>
                  <div className="text-xs text-warm-500">Canonical ID</div>
                  <div className="font-mono text-sm text-midnight-800">{persona.canonical_id}</div>
                </div>
                <div className="ml-auto">
                  <div className="text-xs text-warm-500">Coverage</div>
                  <div className="text-sm font-medium text-midnight-800 capitalize">
                    {persona.coverage_level}
                  </div>
                </div>
              </div>

              {/* Rendered Document */}
              <div className="p-6 bg-midnight-900 rounded-xl overflow-x-auto">
                <pre className="text-warm-100 text-sm font-mono whitespace-pre-wrap leading-relaxed">
                  {persona.document}
                </pre>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  )
}
